














import { computed, defineComponent, Ref, ref, toRefs, watch, watchEffect } from "@vue/composition-api";
import { uniq } from "lodash-es";
import { Group } from "@scrile/api-provider/dist/api/GroupsProvider";
import BasePage from "@/components/BasePage";
import PageTitle from "@/components/PageTitle";
import GroupListSelectionView from "./components/GroupListSelectionView.vue";

type UserIdsMap = Record<string, boolean>;

const useGroupSelection = (groups: Ref<Group[]>) => {
  const toggleSelection = (status: boolean, groupId: string, localState: Ref<string[]>) => {
    if (status) {
      localState.value.push(groupId);
    } else {
      localState.value = localState.value.filter(i => i !== groupId);
    }
  };
  const getUserIdsFromGroups = (comparedGroupIds: string[]): string[] => {
    let out: string[] = [];
    comparedGroupIds.forEach(groupId => {
      const foundGroup = groups.value.find(g => g.id === groupId);
      if (foundGroup) {
        out = out.concat(foundGroup.memberIds);
      }
    });
    return out;
  };
  const unmarkGroup = (id: string, localStata: Ref<string[]>) => {
    localStata.value = localStata.value.filter(i => i !== id);
  };

  const excludeGroupIdsLocal = ref<string[]>([]);
  const excludedUserIdMap = computed<UserIdsMap>(() => {
    const groupUserIds = getUserIdsFromGroups(excludeGroupIdsLocal.value);
    return groupUserIds.reduce<UserIdsMap>((acc, id) => {
      acc[id] = true;
      return acc;
    }, {});
  });

  const selectedGroupIdsLocal = ref<string[]>([]);
  const uniqSelectedUsers = computed<string[]>(() => {
    const groupAndSelectedUserIds = getUserIdsFromGroups(selectedGroupIdsLocal.value);
    return uniq(groupAndSelectedUserIds);
  });

  const selectedUserIds = computed<string[]>(() => {
    return uniqSelectedUsers.value.filter(id => !excludedUserIdMap.value[id]);
  });

  return {
    excludeGroupIdsLocal,
    selectedGroupIdsLocal,
    selectedUserIds,
    toggleSelection,
    unmarkGroup,
  };
};

export default defineComponent({
  name: "GroupListSelection",
  components: { GroupListSelectionView, BasePage, PageTitle },
  props: {
    loading: { type: Boolean, required: true },
    groups: { type: Array as () => Group[], required: true },
    selectedGroupIds: { type: Array as () => string[], required: true },
    excludeGroupIds: { type: Array as () => string[], required: true },
    recipientCount: { type: Number, default: 0 },
    btnIsBottom: { type: Boolean, default: false },
    allowOnlyExclude: { type: Boolean, default: false },
  },
  setup(props, { emit }) {
    const { groups, excludeGroupIds, selectedGroupIds } = toRefs(props);
    const {
      selectedGroupIdsLocal,
      excludeGroupIdsLocal,
      selectedUserIds,
      toggleSelection,
      unmarkGroup,
    } = useGroupSelection(groups);
    const syncStateWithProps = () => {
      selectedGroupIdsLocal.value = [...props.selectedGroupIds];
      excludeGroupIdsLocal.value = [...props.excludeGroupIds];
    };
    emit("setReset", syncStateWithProps);
    watch(
      [selectedGroupIds, excludeGroupIds],
      () => {
        syncStateWithProps();
      },
      { immediate: true }
    );

    const onContinue = () => {
      emit("continue", {
        excludedGroupIds: excludeGroupIdsLocal.value,
        groupIds: selectedGroupIdsLocal.value,
        recipientCount: selectedUserIds.value.length,
      });
    };
    const onMarkGroup = (status: boolean, groupId: string) => {
      toggleSelection(status, groupId, selectedGroupIdsLocal);
      unmarkGroup(groupId, excludeGroupIdsLocal);
    };
    const onMarkExcludedGroup = (status: boolean, groupId: string) => {
      toggleSelection(status, groupId, excludeGroupIdsLocal);
      unmarkGroup(groupId, selectedGroupIdsLocal);
    };

    watchEffect(() => emit("setRecipientCount", selectedUserIds.value.length));

    const allowContinue = computed(
      () =>
        selectedUserIds.value.length > 0 ||
        (props.allowOnlyExclude && selectedGroupIdsLocal.value.length === 0 && excludeGroupIdsLocal.value.length > 0)
    );

    return {
      selectedGroupIdsLocal,
      excludeGroupIdsLocal,
      onContinue,
      onMarkGroup,
      onMarkExcludedGroup,
      allowContinue,
    };
  },
});
